/* eslint-disable max-lines */
import { addMonths } from 'date-fns';

import { formatDate } from 'lib/helpers/dateUtils';
import { hasModule, iaRa, inRn, mString } from 'lib/helpers/utility';
import {
  DEFAULT_TOPS_PAYMENT_OPTIONS,
  LOW_ASSURANCE_FIELD,
  OVERRIDE_FIELDS,
  PAYMENT_METHOD_FIELDS,
  PHASE_FIELDS,
  TOPS_PAYMENT_OPTION_FIELDS,
  TOPS_PAYMENT_OPTION_MODULE_MAP,
  TOPS_PLAN_MONTHS_MIN,
  WEEKS_PER_MONTH
} from 'lib/constants/healthcareProgram/paymentSetup';

import { getPercentOfAllowedCap } from '../../../lib/calculateTotalAmount';

export const getRecurringChargeConfig = (config) =>
  (config?.payment_methods ?? []).find((m) => m.field === PAYMENT_METHOD_FIELDS.RECURRING_CHARGE)
    ?.config ?? null;

// 'bi-weekly' is a cadence, not a unit: the plan builder expresses it as every 2 weeks.
export const resolveFrequency = (frequency) =>
  frequency === 'bi-weekly'
    ? { repeatEvent: 'weekly', repeatEvery: 2 }
    : { repeatEvent: frequency || 'monthly', repeatEvery: 1 };

const computeMaxPayments = (frequency, durationMonths, repeatEvery = 1) => {
  const every = Math.max(1, Number(repeatEvery) || 1);
  if (frequency === 'weekly')
    return Math.max(1, Math.floor((durationMonths * WEEKS_PER_MONTH) / every));
  if (frequency === 'yearly') return Math.max(1, Math.floor(durationMonths / 12 / every));
  return Math.max(1, Math.floor(durationMonths / every));
};

export const getRecurringCaps = (config) => {
  const recurringConfig = getRecurringChargeConfig(config);
  if (!recurringConfig) return null;
  const minAmount = Number(recurringConfig.min_amount);
  const frequencies = iaRa(recurringConfig.frequencies);
  return {
    frequency: frequencies[0] || recurringConfig.frequency || 'monthly',
    frequencies,
    minAmountPerPayment: Number.isFinite(minAmount) && minAmount > 0 ? minAmount : null,
    maxDurationMonths: Math.max(1, Number(recurringConfig.duration_months) || 6),
    lockType: 'installments'
  };
};

export { computeMaxPayments };

export const buildRecurringDefaults = ({ config, balance, timezone, phaseField }) => {
  const recurringConfig = getRecurringChargeConfig(config);
  const frequency =
    iaRa(recurringConfig?.frequencies)[0] || recurringConfig?.frequency || 'monthly';
  const { repeatEvent, repeatEvery } = resolveFrequency(frequency);
  const durationMonths = Number(recurringConfig?.duration_months) || 6;
  const requireApproval = Boolean(recurringConfig?.require_approval);
  const amount = Number(balance) > 0 ? Number(balance) : 0;
  const today = new Date();
  const startDate = formatDate(today, timezone, 'MM/dd/yyyy');

  const numberOfPayments = computeMaxPayments(repeatEvent, durationMonths, repeatEvery);

  const endsDate = formatDate(addMonths(today, durationMonths), timezone, 'MM/dd/yyyy');

  return {
    type: 'installments',
    paymentType: 'number-of-payments',
    amount,
    upfrontPayment: 0,
    startDate,
    repeatEvent,
    repeatEvery,
    numberOfPayments,
    amountPerPayment: null,
    preAuthRequired: true,
    requireApproval,
    endsDate,
    phaseField
  };
};

export const LIVE_PREVIEW_TAB_KEYS = {
  GOOD_FAITH_ESTIMATE: 'good-faith-estimate',
  PATIENT_OPTION_LETTER: 'patient-option-letter',
  TOPS_GFE_POL: 'tops-gfe-pol',
  PRE_AUTH_FORM: 'pre-auth-form'
};

export const LIVE_PREVIEW_TABS = [
  { key: LIVE_PREVIEW_TAB_KEYS.GOOD_FAITH_ESTIMATE, label: 'Good faith estimate' },
  { key: LIVE_PREVIEW_TAB_KEYS.PATIENT_OPTION_LETTER, label: 'Patient option letter' },
  { key: LIVE_PREVIEW_TAB_KEYS.TOPS_GFE_POL, label: 'Tops - Myriad GFE/POL' },
  { key: LIVE_PREVIEW_TAB_KEYS.PRE_AUTH_FORM, label: 'Pre-auth form' }
];

const PHASE_LABEL = {
  [PHASE_FIELDS.IN_NETWORK]: 'In-Network',
  [PHASE_FIELDS.OUT_OF_NETWORK]: 'Out-of-Network',
  [PHASE_FIELDS.SELF_PAY]: 'Self-Pay',
  [OVERRIDE_FIELDS.PAYER]: 'By payer',
  [OVERRIDE_FIELDS.TAG]: 'By appointment tag',
  [LOW_ASSURANCE_FIELD]: 'Low Assurance'
};

const AUTHORIZATION_APPLIED_LABEL = {
  'all-visits-this-year': 'All visits this year',
  'this-visit': 'This visit',
  'let-patient-choose': 'Patient chooses'
};

const CARD_ON_FILE_LABEL = {
  'require-card-on-file': 'COF Required',
  'patient-option': 'COF Optional'
};

const PATIENT_PORTION_CPT_LABEL = {
  'self-pay-amount': 'Self-pay',
  'charge-amount': 'Charge',
  'allowed-amount-ucr': 'UCR'
};

const isRecurringEnabled = (paymentMethods = []) => {
  const recurring = paymentMethods.find((m) => m.field === PAYMENT_METHOD_FIELDS.RECURRING_CHARGE);
  return Boolean(recurring && (recurring.in_person || recurring.remote));
};

const CAP_LIKE_LIMIT_TYPES = new Set([
  'set-dollar-amount',
  'set-higher-allowable-amount',
  'down-payment'
]);

// All cap amounts are CENTS (DB + CurrencyInput store cents). mString
// divides by 100 (cents=true default) for display.
const cap = (amount, label, isCap, hidesAmount = false) => ({ label, isCap, amount, hidesAmount });

export const getFinalAllowedAmountLabel = (phase, selectedScope = null) => {
  const scope = selectedScope || getDefaultScope(phase);
  const visits = !scope || scope === SCOPE_OPTIONS.VISIT ? 'this visit' : 'each covered visit';
  return `the allowed amount for the final services you receive at ${visits}`;
};

export const getPercentOfAllowed = (config) => {
  const percent = Number(config?.percent_above);
  return Number.isFinite(percent) && percent > 0 ? percent : null;
};

const getCapDescriptor = (phase, amounts = {}, oonResponsibility = null, selectedScope = null) => {
  if (!phase) return cap(null, 'the combined allowable amount', false);

  const combinedAllowable = Number(amounts.combined_allowable);
  const selfPay = Number(amounts.self_pay);
  const ucr = Number(amounts.ucr);
  const hasCombinedAllowable = Number.isFinite(combinedAllowable) && combinedAllowable > 0;
  const hasSelfPay = Number.isFinite(selfPay) && selfPay > 0;
  const hasUcr = Number.isFinite(ucr) && ucr > 0;

  if (phase.field === PHASE_FIELDS.IN_NETWORK) {
    const finalAllowedLabel = getFinalAllowedAmountLabel(phase, selectedScope);
    const route = phase.charge_limit?.route_to;
    const config = phase.charge_limit?.config ?? {};
    if (route === 'set-dollar-amount') {
      const v = Number(config.dollar_cap) || 0;
      if (v > 0) return cap(v, mString(v), true);
      return cap(null, 'the combined allowable amount', false);
    }
    const pct = route === 'set-higher-allowable-amount' ? getPercentOfAllowed(config) : null;
    if (pct != null) {
      if (hasCombinedAllowable) {
        const v = getPercentOfAllowedCap(combinedAllowable, pct);
        return cap(v, mString(v), true);
      }
      return cap(null, `a set share of ${finalAllowedLabel}`, true, true);
    }
    if (route === 'full-cost') {
      return hasSelfPay
        ? cap(selfPay, mString(selfPay), true)
        : cap(null, 'the full self-pay cost of services rendered', false);
    }
    return cap(
      hasCombinedAllowable ? combinedAllowable : null,
      finalAllowedLabel,
      hasCombinedAllowable,
      true
    );
  }

  if (phase.field === PHASE_FIELDS.OUT_OF_NETWORK) {
    const limitRoute = phase.charge_limit?.route_to;
    const percentOfAllowed =
      limitRoute === 'set-higher-allowable-amount'
        ? getPercentOfAllowed(phase.charge_limit?.config)
        : null;
    let limitCap = null;
    if (limitRoute === 'set-dollar-amount') {
      const dollarCap = inRn(phase.charge_limit?.config?.dollar_cap);
      limitCap = dollarCap > 0 ? dollarCap : null;
    } else if (percentOfAllowed != null && hasCombinedAllowable) {
      limitCap = getPercentOfAllowedCap(combinedAllowable, percentOfAllowed);
    }

    if (phase.billing_method?.route_to === 'down-payment') {
      const downPayment = inRn(phase.billing_method?.config?.down_payment_amount);
      if (!downPayment)
        return limitCap != null
          ? cap(limitCap, mString(limitCap), true)
          : cap(null, 'the down payment amount', true);
      const amount = limitCap != null ? Math.min(downPayment, limitCap) : downPayment;
      return cap(amount, mString(amount), true);
    }

    if (limitCap != null) return cap(limitCap, mString(limitCap), true);
    if (phase.billing_method?.route_to === 'eligibility-calculation') {
      const netDue = Number(oonResponsibility?.net_due_cents);
      if (Number.isFinite(netDue) && netDue >= 0) {
        return cap(netDue, mString(netDue), true);
      }
    }
    const portion = phase.patient_portion_cpt?.route_to;
    if (portion === 'charge-amount') {
      return hasSelfPay
        ? cap(selfPay, mString(selfPay), false)
        : cap(null, 'the full billed charge amount', false);
    }
    if (portion === 'allowed-amount-ucr') {
      return hasUcr
        ? cap(ucr, mString(ucr), false)
        : cap(null, 'the allowable amount (UCR)', false);
    }
    return hasSelfPay
      ? cap(selfPay, mString(selfPay), false)
      : cap(null, `the practice's self-pay amount per CPT code`, false);
  }

  if (phase.field === PHASE_FIELDS.SELF_PAY) {
    const route = phase.charge_limit?.route_to;
    const config = phase.charge_limit?.config ?? {};
    if (route === 'set-dollar-amount') {
      const v = Number(config.dollar_cap) || 0;
      if (v > 0) return cap(v, mString(v), true);
      return cap(null, 'the full cost of treatments actually received', false);
    }
    return cap(null, 'the full cost of treatments actually received', false);
  }

  return cap(null, 'the combined allowable amount', false);
};

const getScopeClause = (route) => {
  if (route === 'all-visits-this-year') return ' for this and all visits this calendar year';
  if (route === 'let-patient-choose') {
    return ' for the visits authorized on the Pre-Authorized Healthcare Form';
  }
  return '';
};

export const getHealthcareProgramDisclosureCopy = (
  phase,
  amounts,
  oonResponsibility = null,
  selectedScope = null
) => {
  const choiceClause =
    phase?.card_on_file?.route_to === 'require-card-on-file' ? 'are required to' : 'may choose to';
  const scopeClause = getScopeClause(phase?.authorization_applied?.route_to);
  const { label: capLabel, isCap } = getCapDescriptor(
    phase,
    amounts,
    oonResponsibility,
    selectedScope
  );

  return { choiceClause, scopeClause, capLabel, isCap };
};

export const isCapLikeLimit = (route) => CAP_LIKE_LIMIT_TYPES.has(route);

export const getCapLabel = (phase, amounts, oonResponsibility = null, selectedScope = null) => {
  const { label, isCap, amount, hidesAmount } = getCapDescriptor(
    phase,
    amounts,
    oonResponsibility,
    selectedScope
  );
  return { capLabel: label, isCap, capAmount: amount, hidesAmount };
};

export const isSelfPayPhase = (phase) => phase?.field === PHASE_FIELDS.SELF_PAY;

export const getLivePreviewPhaseChips = (phase, paymentMethods) => {
  if (!phase) return [];

  const chips = [];

  const phaseLabel = PHASE_LABEL[phase.chipField] ?? PHASE_LABEL[phase.field];
  if (phaseLabel) chips.push(phaseLabel);

  const cofLabel = CARD_ON_FILE_LABEL[phase.card_on_file?.route_to];
  if (cofLabel) chips.push(cofLabel);

  const authLabel = AUTHORIZATION_APPLIED_LABEL[phase.authorization_applied?.route_to];
  if (authLabel) chips.push(authLabel);

  if (phase.field === PHASE_FIELDS.OUT_OF_NETWORK) {
    const ppc = PATIENT_PORTION_CPT_LABEL[phase.patient_portion_cpt?.route_to];
    if (ppc) chips.push(ppc);
  }

  if (isRecurringEnabled(paymentMethods)) chips.push('Recurring');

  return chips;
};

export const SCOPE_OPTIONS = {
  VISIT: 'this-visit',
  YEAR: 'all-visits-this-year',
  ONGOING: 'ongoing-treatments'
};

const ROUTE_TO_SCOPE = {
  'this-visit': SCOPE_OPTIONS.VISIT,
  'all-visits-this-year': SCOPE_OPTIONS.YEAR,
  'let-patient-choose': SCOPE_OPTIONS.VISIT
};

export const getDefaultScope = (phase) =>
  ROUTE_TO_SCOPE[phase?.authorization_applied?.route_to] ?? null;

export const getScopeLabel = (route) => {
  if (route === SCOPE_OPTIONS.YEAR) return 'all visits in the 12 months after signing';
  if (route === SCOPE_OPTIONS.VISIT) return 'this visit only';
  if (route === SCOPE_OPTIONS.ONGOING) return 'ongoing treatments';
  return '';
};

export const allowedCards = [
  { icon: 'cards-visuals-visa', id: 1 },
  { icon: 'cards-visuals-mastercard', id: 2 },
  { icon: 'cards-visuals-amex', id: 3 },
  { icon: 'cards-visuals-discover', id: 4 }
];

export const LETTER_VIEWS = {
  GOOD_FAITH_ESTIMATE: LIVE_PREVIEW_TAB_KEYS.GOOD_FAITH_ESTIMATE,
  PATIENT_OPTION_LETTER: LIVE_PREVIEW_TAB_KEYS.PATIENT_OPTION_LETTER,
  TOPS_GFE_POL: LIVE_PREVIEW_TAB_KEYS.TOPS_GFE_POL,
  PRE_AUTH_FORM: LIVE_PREVIEW_TAB_KEYS.PRE_AUTH_FORM
};

export const getLetterView = ({ showPreAuthForm, letterType }) => {
  if (showPreAuthForm) return LETTER_VIEWS.PRE_AUTH_FORM;
  if (letterType === LETTER_VIEWS.PATIENT_OPTION_LETTER) return LETTER_VIEWS.PATIENT_OPTION_LETTER;
  if (letterType === LETTER_VIEWS.TOPS_GFE_POL) return LETTER_VIEWS.TOPS_GFE_POL;
  return LETTER_VIEWS.GOOD_FAITH_ESTIMATE;
};

const TOPS_OPTION_ORDER = [
  TOPS_PAYMENT_OPTION_FIELDS.RESERVATION_SAVINGS,
  TOPS_PAYMENT_OPTION_FIELDS.HALF_DOWN,
  TOPS_PAYMENT_OPTION_FIELDS.PAYMENT_PLAN
];

const TOPS_OPTION_TILES = {
  [TOPS_PAYMENT_OPTION_FIELDS.RESERVATION_SAVINGS]: {
    title: 'Reservation savings',
    icon: 'discount-1-filled',
    caption: ({ discount_percent: discount }) =>
      `Save ${discount}% when you pay your balance in full now. Outside financing not eligible.`
  },
  [TOPS_PAYMENT_OPTION_FIELDS.HALF_DOWN]: {
    title: 'Half down, remaining later',
    icon: 'cash-filled',
    caption: ({ first_payment_percent: firstPayment }) =>
      `Pay ${firstPayment}% now, the remainder on a later date you choose.`
  },
  [TOPS_PAYMENT_OPTION_FIELDS.PAYMENT_PLAN]: {
    title: 'Payment plan option',
    icon: 'recurring-charges-filled',
    caption: ({ max_months: maxMonths }) =>
      `Monthly installments from ${TOPS_PLAN_MONTHS_MIN} up to ${maxMonths} months, based on credit approval.`
  }
};

export const isTopsOptionModuleEnabled = (practiceModules, field) => {
  const path = TOPS_PAYMENT_OPTION_MODULE_MAP[field];
  if (!path || !practiceModules) return true;
  return !!hasModule(practiceModules, path);
};

export const getTopsLetterOptions = ({ view, config, practiceModules }) => {
  if (view !== LETTER_VIEWS.TOPS_GFE_POL) return [];

  return TOPS_OPTION_ORDER.reduce((tiles, field) => {
    const option = {
      ...DEFAULT_TOPS_PAYMENT_OPTIONS[field],
      ...(config?.tops_payment_options?.[field] ?? {})
    };
    if (!option.enabled || !isTopsOptionModuleEnabled(practiceModules, field)) return tiles;

    const tile = TOPS_OPTION_TILES[field];
    tiles.push({ field, title: tile.title, icon: tile.icon, caption: tile.caption(option) });
    return tiles;
  }, []);
};

export const PREVIEW_SIMULATED_AMOUNTS = {
  combined_allowable: 28200,
  self_pay: 34500,
  ucr: 31000
};

export const PREVIEW_SIMULATED_BALANCE = 28200;

const SELF_PAY_TYPES = new Set(['self_pay', 'cash']);

export const isSelfPayProcedure = (proc) =>
  SELF_PAY_TYPES.has(proc?.type) || proc?.charge_type === 'self_pay';

export const getCoverageSummary = ({ appointment, totalAmount }) => {
  const invoice = appointment?.invoice;
  const procedures = iaRa(invoice?.procedures);
  const products = iaRa(invoice?.products);

  const insuranceSavings = procedures.reduce(
    (acc, proc) =>
      isSelfPayProcedure(proc)
        ? acc
        : acc +
          Math.max(inRn(proc?.charge) * (Number(proc?.units) || 1) - inRn(proc?.pt_balance), 0),
    0
  );

  const payments = inRn(invoice?.amount_paid);
  const patientPortion = inRn(totalAmount);

  const invoiceTax = inRn(invoice?.tax);
  const surchargeAmount = inRn(Math.round(appointment?.surcharge_amount));
  const taxesAndFees = invoiceTax + surchargeAmount;

  const totalCost = inRn(invoice?.total_amount) + insuranceSavings;
  const discounts = Math.max(0, totalCost - payments + taxesAndFees - patientPortion);
  const patientShare = Math.max(0, patientPortion - taxesAndFees);

  const grossTotal = totalCost + taxesAndFees;
  const toBasisPoints = (amount) => (grossTotal > 0 ? (amount / grossTotal) * 10000 : 0);

  const deductible = inRn(invoice?.last_applied?.deductible?.value);
  const coPay = inRn(invoice?.last_applied?.co_payment?.value);
  const coInsurance = inRn(invoice?.last_applied?.co_insurance?.value);

  const patientResponsibility = inRn(invoice?.total_amount) + invoiceTax;

  const hasSelfPay =
    invoice?.last_applied?.type === 'self_pay' || procedures.some(isSelfPayProcedure);

  const isFullyCovered =
    totalCost > 0 &&
    insuranceSavings > 0 &&
    !hasSelfPay &&
    deductible + coPay + coInsurance <= 0 &&
    patientResponsibility <= 0;

  return {
    serviceCount: procedures.length + products.length,
    totalCost,
    insuranceSavings,
    payments,
    discounts,
    taxesAndFees,
    patientPortion,
    patientShare,
    deductible,
    coPay,
    coInsurance,
    paymentsBasisPoints: toBasisPoints(payments),
    discountsBasisPoints: toBasisPoints(discounts),
    taxesBasisPoints: toBasisPoints(taxesAndFees),
    patientCoverageBasisPoints: toBasisPoints(patientShare),
    isFullyCovered,
    isNothingDue: patientPortion <= 0
  };
};

export const PREVIEW_SIMULATED_INSURANCE_SAVINGS = 21000;

export const getPreviewCoverageSummary = () => {
  const discounts = PREVIEW_SIMULATED_INSURANCE_SAVINGS;
  const patientPortion = PREVIEW_SIMULATED_BALANCE;
  const totalCost = patientPortion + discounts;
  const toBasisPoints = (amount) => (totalCost > 0 ? (amount / totalCost) * 10000 : 0);

  return {
    serviceCount: 4,
    totalCost,
    payments: 0,
    discounts,
    taxesAndFees: 0,
    patientPortion,
    deductible: 1000,
    coPay: 2000,
    coInsurance: 4000,
    paymentsBasisPoints: 0,
    discountsBasisPoints: toBasisPoints(discounts),
    taxesBasisPoints: 0,
    patientCoverageBasisPoints: toBasisPoints(patientPortion),
    isFullyCovered: false,
    isNothingDue: false
  };
};

export const orderLetterMethods = (methods) => {
  const ordered = iaRa(methods).filter(
    (method) => method.id !== PAYMENT_METHOD_FIELDS.DEFER_PAYMENT
  );
  const deferred = iaRa(methods).filter(
    (method) => method.id === PAYMENT_METHOD_FIELDS.DEFER_PAYMENT
  );

  return [...ordered, ...deferred];
};

export const getTopsTilesAnchorIndex = (methods) => {
  const ordered = iaRa(methods);
  const recurringIndex = ordered.findIndex(
    (method) => method.id === PAYMENT_METHOD_FIELDS.RECURRING_CHARGE
  );
  if (recurringIndex !== -1) return recurringIndex;

  const deferredIndex = ordered.findIndex(
    (method) => method.id === PAYMENT_METHOD_FIELDS.DEFER_PAYMENT
  );
  return deferredIndex !== -1 ? deferredIndex : ordered.length;
};

export const getCostShareLabel = ({ deductible, coPay, coInsurance, surcharge, gratuity } = {}) => {
  const parts = [
    inRn(deductible) > 0 && `deductible ${mString(deductible)}`,
    inRn(coPay) > 0 && `copay ${mString(coPay)}`,
    inRn(coInsurance) > 0 && `coinsurance ${mString(coInsurance)}`,
    inRn(surcharge) > 0 && `technology fee ${mString(surcharge)}`,
    inRn(gratuity) > 0 && `gratuity ${mString(gratuity)}`
  ].filter(Boolean);

  if (!parts.length) return null;
  const label = parts.join(' + ');
  return `${label[0].toUpperCase()}${label.slice(1)}`;
};
